import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  ShoppingBag,
  Download,
  Mail,
  Lock,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  CreditCard,
  X,
} from 'lucide-react';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import './BuyerAccount.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DownloadLink {
  token: string;
  expires_at: string;
  download_count: number;
  max_downloads: number;
}

interface OrderItem {
  product_name: string;
  price_at_purchase: number;
  downloads: DownloadLink[];
}

interface BuyerOrder {
  order_id: string;
  created_at: string;
  total: number;
  currency: string;
  status: string;
  items: OrderItem[];
}

interface BuyerSubscription {
  subscription_id: string;
  product_name: string;
  billing_period: string;
  next_renewal_at: string | null;
  status: string;
}

type PageState = 'loading' | 'unauthenticated' | 'authenticated';
type AuthView = 'login' | 'resend';
type ActiveTab = 'orders' | 'subscriptions';

// ---------------------------------------------------------------------------
// Buyer API helper — uses buyer JWT, not AiMe user JWT
// ---------------------------------------------------------------------------

const buyerApi = axios.create({
  baseURL: '/api',
});

function getBuyerToken(): string | null {
  return localStorage.getItem('buyer_token');
}

function setBuyerToken(token: string) {
  localStorage.setItem('buyer_token', token);
}

function clearBuyerToken() {
  localStorage.removeItem('buyer_token');
}

function authHeaders() {
  const token = getBuyerToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface DownloadItemProps {
  item: OrderItem;
  currency: string;
  onRequestNewLinks: (email: string) => void;
}

const DownloadItem: React.FC<DownloadItemProps> = ({ item, currency, onRequestNewLinks }) => {
  const now = new Date();

  return (
    <div className="buyer-order-item">
      <div className="buyer-order-item-header">
        <span className="buyer-product-name">{item.product_name}</span>
        <span className="buyer-product-price">
          {currency} {(item.price_at_purchase / 100).toFixed(2)}
        </span>
      </div>
      <div className="buyer-downloads">
        {item.downloads.length === 0 && (
          <span className="buyer-no-downloads">No download links available.</span>
        )}
        {item.downloads.map((dl) => {
          const expired = dl.expires_at ? new Date(dl.expires_at) < now : false;
          const exhausted = dl.max_downloads > 0 && dl.download_count >= dl.max_downloads;
          const unavailable = expired || exhausted;
          const remaining = dl.max_downloads > 0 ? dl.max_downloads - dl.download_count : null;

          return (
            <div key={dl.token} className="buyer-download-row">
              <div className="buyer-download-meta">
                {dl.max_downloads > 0 && (
                  <span className="buyer-download-count">
                    {dl.download_count} of {dl.max_downloads} downloads used
                    {remaining !== null && remaining > 0 && (
                      <span className="buyer-remaining"> ({remaining} remaining)</span>
                    )}
                  </span>
                )}
                {expired && (
                  <span className="buyer-link-status expired">
                    <AlertCircle size={14} /> Link expired
                  </span>
                )}
                {!expired && exhausted && (
                  <span className="buyer-link-status exhausted">
                    <AlertCircle size={14} /> Download limit reached
                  </span>
                )}
              </div>
              <div className="buyer-download-actions">
                {unavailable ? (
                  <GlassButton
                    variant="secondary"
                    size="sm"
                    icon={<Mail size={14} />}
                    onClick={() => onRequestNewLinks('')}
                  >
                    Request new links
                  </GlassButton>
                ) : (
                  <a
                    className="buyer-download-btn"
                    href={`/api/shop/download/${dl.token}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Download size={14} />
                    Download
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Cancel Subscription Modal
// ---------------------------------------------------------------------------

interface CancelModalProps {
  subscriptionId: string;
  productName: string;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}

const CancelModal: React.FC<CancelModalProps> = ({
  subscriptionId,
  productName,
  onConfirm,
  onClose,
}) => {
  const [cancelling, setCancelling] = useState(false);

  const handleConfirm = async () => {
    setCancelling(true);
    try {
      await onConfirm();
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="buyer-modal-overlay" onClick={onClose}>
      <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
      <GlassCard
        className="buyer-modal"
        padding="lg"
      >
        <div className="buyer-modal-header">
          <h3>Cancel Subscription</h3>
          <GlassButton variant="ghost" size="sm" icon={<X size={16} />} onClick={onClose} />
        </div>
        <p className="buyer-modal-body">
          Are you sure you want to cancel your subscription to <strong>{productName}</strong>? You
          will retain access until the end of your current billing period.
        </p>
        <div className="buyer-modal-actions">
          <GlassButton variant="secondary" onClick={onClose} disabled={cancelling}>
            Keep Subscription
          </GlassButton>
          <GlassButton variant="danger" loading={cancelling} onClick={handleConfirm}>
            Cancel Subscription
          </GlassButton>
        </div>
      </GlassCard>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const BuyerAccount: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Page state
  const [pageState, setPageState] = useState<PageState>('loading');
  const [authView, setAuthView] = useState<AuthView>('login');
  const [activeTab, setActiveTab] = useState<ActiveTab>('orders');

  // Auth form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authSuccess, setAuthSuccess] = useState('');

  // Data state
  const [orders, setOrders] = useState<BuyerOrder[]>([]);
  const [subscriptions, setSubscriptions] = useState<BuyerSubscription[]>([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState('');

  // Create account (set password) state
  const [showSetPassword, setShowSetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [setPasswordLoading, setSetPasswordLoading] = useState(false);
  const [setPasswordError, setSetPasswordError] = useState('');
  const [setPasswordSuccess, setSetPasswordSuccess] = useState('');

  // Cancel modal state
  const [cancelModal, setCancelModal] = useState<{ id: string; name: string } | null>(null);

  // Order pending poll state
  const [pendingOrder, setPendingOrder] = useState<string | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  // Expanded order cards
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());

  // ---------------------------------------------------------------------------
  // Fetch data after auth
  // ---------------------------------------------------------------------------

  const fetchData = useCallback(async () => {
    setDataLoading(true);
    setDataError('');
    try {
      const [ordersRes, subsRes] = await Promise.all([
        buyerApi.get<BuyerOrder[]>('/buyer/orders', { headers: authHeaders() }),
        buyerApi.get<BuyerSubscription[]>('/buyer/subscriptions', { headers: authHeaders() }),
      ]);
      setOrders(ordersRes.data);
      setSubscriptions(subsRes.data);
    } catch (err: any) {
      if (err?.response?.status === 401) {
        clearBuyerToken();
        setPageState('unauthenticated');
      } else {
        setDataError('Failed to load your purchases. Please try again.');
      }
    } finally {
      setDataLoading(false);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // On mount: check token in URL or localStorage
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const init = async () => {
      // Check for ?order=pending&email=... redirect from checkout
      const orderParam = searchParams.get('order');
      const emailParam = searchParams.get('email');
      if (orderParam === 'pending' && emailParam) {
        setPendingOrder(orderParam);
        setPendingEmail(emailParam);
        setPageState('loading');
        // Poll will handle transition
        return;
      }

      // Check for magic link token in URL
      const magicToken = searchParams.get('token');
      if (magicToken) {
        try {
          const res = await buyerApi.post<{ buyer_token: string }>('/buyer/auth/magic', {
            account_token: magicToken,
          });
          setBuyerToken(res.data.buyer_token);
          // Remove token from URL
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('token');
          setSearchParams(newParams, { replace: true });
          await fetchData();
          setPageState('authenticated');
        } catch {
          setAuthError('This magic link is invalid or has expired. Please sign in below.');
          setPageState('unauthenticated');
        }
        return;
      }

      // Check localStorage for existing buyer token
      const stored = getBuyerToken();
      if (stored) {
        try {
          await fetchData();
          setPageState('authenticated');
        } catch {
          clearBuyerToken();
          setPageState('unauthenticated');
        }
      } else {
        setPageState('unauthenticated');
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------------
  // Poll for pending order
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!pendingOrder || !pendingEmail) return;

    let attempts = 0;
    const maxAttempts = 30; // 30 * 2s = 60s max

    const poll = setInterval(async () => {
      attempts++;
      try {
        const res = await buyerApi.get<{ ready: boolean; buyer_token?: string }>(
          `/buyer/orders/pending?email=${encodeURIComponent(pendingEmail)}`
        );
        if (res.data.ready && res.data.buyer_token) {
          clearInterval(poll);
          setBuyerToken(res.data.buyer_token);
          setPendingOrder(null);
          setPendingEmail(null);
          await fetchData();
          setPageState('authenticated');
        }
      } catch {
        // Ignore transient errors during polling
      }
      if (attempts >= maxAttempts) {
        clearInterval(poll);
        setPendingOrder(null);
        setPendingEmail(null);
        setPageState('unauthenticated');
      }
    }, 2000);

    return () => clearInterval(poll);
  }, [pendingOrder, pendingEmail, fetchData]);

  // ---------------------------------------------------------------------------
  // Auth handlers
  // ---------------------------------------------------------------------------

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);
    try {
      const res = await buyerApi.post<{ buyer_token: string }>('/buyer/auth/login', {
        email,
        password,
      });
      setBuyerToken(res.data.buyer_token);
      await fetchData();
      setPageState('authenticated');
    } catch (err: any) {
      setAuthError(
        getApiError(err, 'Invalid email or password. Please try again.')
      );
    } finally {
      setAuthLoading(false);
    }
  };

  const handleResendDownloads = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setAuthSuccess('');
    setAuthLoading(true);
    try {
      await buyerApi.post('/buyer/resend-downloads', { email });
      setAuthSuccess(
        'If an account exists for that email, download links have been sent. Check your inbox.'
      );
    } catch (err: any) {
      setAuthError(getApiError(err, 'Failed to send. Please try again.'));
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSignOut = () => {
    clearBuyerToken();
    setOrders([]);
    setSubscriptions([]);
    setPageState('unauthenticated');
    setAuthView('login');
  };

  // ---------------------------------------------------------------------------
  // Request new download links
  // ---------------------------------------------------------------------------

  const handleRequestNewLinks = async (emailOverride: string) => {
    const targetEmail = emailOverride || email;
    if (!targetEmail) {
      setDataError('Please sign in to request new download links.');
      return;
    }
    try {
      await buyerApi.post(
        '/buyer/resend-downloads',
        { email: targetEmail },
        { headers: authHeaders() }
      );
      setDataError('');
      // Show transient success
      setDataError(''); // clear error
    } catch (err: any) {
      setDataError(getApiError(err, 'Failed to send new links. Please try again.'));
    }
  };

  // ---------------------------------------------------------------------------
  // Set password handler
  // ---------------------------------------------------------------------------

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setSetPasswordError('');
    if (newPassword !== confirmPassword) {
      setSetPasswordError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setSetPasswordError('Password must be at least 8 characters.');
      return;
    }
    setSetPasswordLoading(true);
    try {
      await buyerApi.post(
        '/buyer/auth/create-account',
        { password: newPassword },
        { headers: authHeaders() }
      );
      setSetPasswordSuccess('Password set. You can now sign in with your email and password.');
      setShowSetPassword(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setSetPasswordError(getApiError(err, 'Failed to set password. Please try again.'));
    } finally {
      setSetPasswordLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Cancel subscription
  // ---------------------------------------------------------------------------

  const handleCancelSubscription = async () => {
    if (!cancelModal) return;
    try {
      await buyerApi.delete(`/buyer/subscriptions/${cancelModal.id}`, {
        headers: authHeaders(),
      });
      setSubscriptions((prev) =>
        prev.map((s) =>
          s.subscription_id === cancelModal.id ? { ...s, status: 'cancelled' } : s
        )
      );
      setCancelModal(null);
    } catch (err: any) {
      setDataError(getApiError(err, 'Failed to cancel subscription.'));
      setCancelModal(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Toggle order expanded
  // ---------------------------------------------------------------------------

  const toggleOrder = (orderId: string) => {
    setExpandedOrders((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };

  // ---------------------------------------------------------------------------
  // Format helpers
  // ---------------------------------------------------------------------------

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  const truncateId = (id: string) => (id.length > 12 ? `${id.slice(0, 8)}...` : id);

  // ---------------------------------------------------------------------------
  // Render: loading / pending
  // ---------------------------------------------------------------------------

  if (pageState === 'loading' || pendingOrder) {
    return (
      <div className="buyer-page">
        <div className="buyer-page-bg" />
        <div className="buyer-container">
          <div className="buyer-logo-row">
            <img src="/aim-logo.png" alt="AiMe" className="buyer-logo" />
          </div>
          <GlassCard className="buyer-card" padding="lg">
            <div className="buyer-loading">
              <RefreshCw size={32} className="buyer-spinner" />
              {pendingOrder ? (
                <>
                  <h2>Processing your order...</h2>
                  <p className="buyer-loading-sub">
                    We're confirming your purchase and preparing your downloads. This usually takes
                    a few seconds.
                  </p>
                </>
              ) : (
                <p>Loading your account...</p>
              )}
            </div>
          </GlassCard>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: unauthenticated
  // ---------------------------------------------------------------------------

  if (pageState === 'unauthenticated') {
    return (
      <div className="buyer-page">
        <div className="buyer-page-bg" />
        <div className="buyer-container">
          <div className="buyer-logo-row">
            <img src="/aim-logo.png" alt="AiMe" className="buyer-logo" />
          </div>

          <GlassCard className="buyer-card" padding="lg">
            <div className="buyer-card-header">
              <ShoppingBag size={28} className="buyer-card-icon" />
              <h1 className="buyer-heading">Your Purchases</h1>
            </div>

            {authError && (
              <div className="buyer-alert buyer-alert-error">
                <AlertCircle size={16} />
                {authError}
              </div>
            )}
            {authSuccess && (
              <div className="buyer-alert buyer-alert-success">
                <CheckCircle size={16} />
                {authSuccess}
              </div>
            )}

            {authView === 'login' ? (
              <form className="buyer-form" onSubmit={handleLogin}>
                <div className="buyer-field">
                  <label htmlFor="buyer-email">Email</label>
                  <div className="buyer-input-wrap">
                    <Mail size={16} className="buyer-input-icon" />
                    <input
                      id="buyer-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      autoComplete="email"
                    />
                  </div>
                </div>
                <div className="buyer-field">
                  <label htmlFor="buyer-password">Password</label>
                  <div className="buyer-input-wrap">
                    <Lock size={16} className="buyer-input-icon" />
                    <input
                      id="buyer-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Your password"
                      autoComplete="current-password"
                    />
                  </div>
                </div>
                <GlassButton
                  variant="primary"
                  type="submit"
                  loading={authLoading}
                  style={{ width: '100%' }}
                >
                  Sign In
                </GlassButton>

                <div className="buyer-magic-note">
                  <Mail size={14} />
                  Check your purchase confirmation email for a direct access link
                </div>

                <div className="buyer-auth-alt">
                  <button
                    type="button"
                    className="buyer-text-link"
                    onClick={() => {
                      setAuthView('resend');
                      setAuthError('');
                      setAuthSuccess('');
                    }}
                  >
                    No account? Enter your email to resend download links
                  </button>
                </div>
              </form>
            ) : (
              <form className="buyer-form" onSubmit={handleResendDownloads}>
                <p className="buyer-resend-note">
                  Enter the email address you used at checkout and we'll resend your download links.
                </p>
                <div className="buyer-field">
                  <label htmlFor="buyer-resend-email">Email address</label>
                  <div className="buyer-input-wrap">
                    <Mail size={16} className="buyer-input-icon" />
                    <input
                      id="buyer-resend-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      autoComplete="email"
                    />
                  </div>
                </div>
                <GlassButton
                  variant="primary"
                  type="submit"
                  loading={authLoading}
                  style={{ width: '100%' }}
                >
                  Send Download Links
                </GlassButton>
                <div className="buyer-auth-alt">
                  <button
                    type="button"
                    className="buyer-text-link"
                    onClick={() => {
                      setAuthView('login');
                      setAuthError('');
                      setAuthSuccess('');
                    }}
                  >
                    Back to sign in
                  </button>
                </div>
              </form>
            )}
          </GlassCard>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: authenticated
  // ---------------------------------------------------------------------------

  return (
    <div className="buyer-page">
      <div className="buyer-page-bg" />

      {cancelModal && (
        <CancelModal
          subscriptionId={cancelModal.id}
          productName={cancelModal.name}
          onConfirm={handleCancelSubscription}
          onClose={() => setCancelModal(null)}
        />
      )}

      <div className="buyer-container">
        <div className="buyer-logo-row">
          <img src="/aim-logo.png" alt="AiMe" className="buyer-logo" />
          <GlassButton variant="secondary" size="sm" onClick={handleSignOut}>
            Sign out
          </GlassButton>
        </div>

        <GlassCard className="buyer-card" padding="none">
          <div className="buyer-card-header" style={{ padding: 'var(--space-6)' }}>
            <ShoppingBag size={24} className="buyer-card-icon" />
            <h1 className="buyer-heading">Your Purchases</h1>
          </div>

          {/* Tabs */}
          <div className="buyer-tabs">
            <button
              className={`buyer-tab${activeTab === 'orders' ? ' active' : ''}`}
              onClick={() => setActiveTab('orders')}
            >
              <Download size={15} /> Orders &amp; Downloads
            </button>
            <button
              className={`buyer-tab${activeTab === 'subscriptions' ? ' active' : ''}`}
              onClick={() => setActiveTab('subscriptions')}
            >
              <CreditCard size={15} /> Subscriptions
            </button>
          </div>

          <div className="buyer-tab-content">
            {dataLoading ? (
              <div className="buyer-loading small">
                <RefreshCw size={20} className="buyer-spinner" />
                <p>Loading...</p>
              </div>
            ) : dataError ? (
              <div className="buyer-alert buyer-alert-error" style={{ margin: 'var(--space-4)' }}>
                <AlertCircle size={16} /> {dataError}
              </div>
            ) : activeTab === 'orders' ? (
              /* ---- Orders tab ---- */
              <div className="buyer-orders-list">
                {orders.length === 0 ? (
                  <div className="buyer-empty">
                    <ShoppingBag size={40} />
                    <p>No purchases found for this account.</p>
                  </div>
                ) : (
                  orders.map((order) => {
                    const expanded = expandedOrders.has(order.order_id);
                    return (
                      <div key={order.order_id} className="buyer-order-card">
                        <button
                          className="buyer-order-summary"
                          onClick={() => toggleOrder(order.order_id)}
                        >
                          <div className="buyer-order-meta">
                            <span className="buyer-order-date">{formatDate(order.created_at)}</span>
                            <span className="buyer-order-id">#{truncateId(order.order_id)}</span>
                            <span
                              className={`buyer-order-status status-${order.status.toLowerCase()}`}
                            >
                              {order.status}
                            </span>
                          </div>
                          <div className="buyer-order-right">
                            <span className="buyer-order-total">
                              {order.currency} {(order.total / 100).toFixed(2)}
                            </span>
                            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </div>
                        </button>
                        {expanded && (
                          <div className="buyer-order-items">
                            {order.items.map((item, idx) => (
                              <DownloadItem
                                key={idx}
                                item={item}
                                currency={order.currency}
                                onRequestNewLinks={handleRequestNewLinks}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            ) : (
              /* ---- Subscriptions tab ---- */
              <div className="buyer-subscriptions-list">
                {subscriptions.length === 0 ? (
                  <div className="buyer-empty">
                    <CreditCard size={40} />
                    <p>No active subscriptions.</p>
                  </div>
                ) : (
                  subscriptions.map((sub) => (
                    <div key={sub.subscription_id} className="buyer-sub-card">
                      <div className="buyer-sub-info">
                        <span className="buyer-sub-name">{sub.product_name}</span>
                        <span className="buyer-sub-period">{sub.billing_period}</span>
                        {sub.next_renewal_at && (
                          <span className="buyer-sub-renewal">
                            Renews {formatDate(sub.next_renewal_at)}
                          </span>
                        )}
                        <span
                          className={`buyer-sub-status status-${sub.status.toLowerCase()}`}
                        >
                          {sub.status}
                        </span>
                      </div>
                      {sub.status !== 'cancelled' && (
                        <GlassButton
                          variant="secondary"
                          size="sm"
                          onClick={() =>
                            setCancelModal({ id: sub.subscription_id, name: sub.product_name })
                          }
                        >
                          Cancel
                        </GlassButton>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </GlassCard>

        {/* Set password / create account section */}
        <GlassCard className="buyer-card buyer-set-password-card" padding="lg">
          <div className="buyer-set-password-header">
            <Lock size={18} />
            <h3>Secure your account</h3>
            <GlassButton
              variant="ghost"
              size="sm"
              icon={showSetPassword ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              onClick={() => setShowSetPassword((v) => !v)}
            />
          </div>
          <p className="buyer-set-password-hint">
            Set a password to sign in next time without needing an email link.
          </p>
          {setPasswordSuccess && (
            <div className="buyer-alert buyer-alert-success">
              <CheckCircle size={16} /> {setPasswordSuccess}
            </div>
          )}
          {showSetPassword && (
            <form className="buyer-form" onSubmit={handleSetPassword}>
              {setPasswordError && (
                <div className="buyer-alert buyer-alert-error">
                  <AlertCircle size={16} /> {setPasswordError}
                </div>
              )}
              <div className="buyer-field">
                <label htmlFor="new-password">New Password</label>
                <div className="buyer-input-wrap">
                  <Lock size={16} className="buyer-input-icon" />
                  <input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    required
                    autoComplete="new-password"
                  />
                </div>
              </div>
              <div className="buyer-field">
                <label htmlFor="confirm-password">Confirm Password</label>
                <div className="buyer-input-wrap">
                  <Lock size={16} className="buyer-input-icon" />
                  <input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repeat your password"
                    required
                    autoComplete="new-password"
                  />
                </div>
              </div>
              <GlassButton
                variant="primary"
                type="submit"
                loading={setPasswordLoading}
                style={{ width: '100%' }}
              >
                Set Password
              </GlassButton>
            </form>
          )}
        </GlassCard>
      </div>
    </div>
  );
};

export default BuyerAccount;
